// sales-files-v1
import { sendWithCostCheck } from "@/lib/belowCost";
import { useEffect, useState } from "react";
import { FileText, Loader2, Mail, Paperclip, Printer } from "lucide-react";
import { openPdf } from "@/lib/print";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ProposalFilesDialog, fmtSize } from "@/components/sales/ProposalFilesDialog";

/** Send a proposal to the client by email: quotation PDF + the design drawings. */
export function ProposalSendDialog({ row, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ to: "", cc: "", subject: "", message: "", attach: true });
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [files, setFiles] = useState(false);
  const load = () => api.get(`/pipeline/proposals/${row.id}/email-defaults`).then(({ data }) => {
    setD(data); setF((x) => ({ ...x, to: x.to || data.to, subject: x.subject || data.subject, message: data.message }));
  }).catch((ex) => setErr(getErrorMessage(ex)));
  useEffect(() => { load(); }, [row.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const send = async () => {
    setBusy("send"); setErr("");
    try {
      const { data } = await sendWithCostCheck((allow) => api.post(`/pipeline/proposals/${row.id}/email`, { to: f.to, cc: f.cc || null, subject: f.subject, message: f.message, attach_design: f.attach, allow_below_cost: allow }));
      toast.success(`Emailed to ${data.sent_to}${data.drawings ? ` with ${data.drawings} drawing(s)` : ""}`); onDone(); onClose();
    } catch (ex) { if (!ex?.cancelled) setErr(getErrorMessage(ex)); } finally { setBusy(""); }
  };
  const markOnly = async () => {
    setBusy("mark"); setErr("");
    try {
      await sendWithCostCheck((allow) => api.post(`/pipeline/proposals/${row.id}/send`, null, { params: allow ? { allow_below_cost: true } : {} }));
      toast.success(`${row.code} marked as sent`); onDone(); onClose();
    } catch (ex) { if (!ex?.cancelled) setErr(getErrorMessage(ex)); } finally { setBusy(""); }
  };
  const n = d?.files?.length || 0;
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="send-dialog">
          <DialogHeader><DialogTitle>Send proposal · {row.code}</DialogTitle></DialogHeader>
          {!d ? <p className="py-6 text-center text-sm text-muted-foreground">{err || "Loading…"}</p> : (
            <div className="grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>To *</Label><Input value={f.to} onChange={set("to")} placeholder="client@example.com" className="mt-1.5" data-testid="send-to" /></div>
                <div><Label>Cc</Label><Input value={f.cc} onChange={set("cc")} placeholder="optional" className="mt-1.5" /></div>
              </div>
              <div><Label>Subject</Label><Input value={f.subject} onChange={set("subject")} className="mt-1.5" /></div>
              <div><Label>Message</Label><Textarea rows={7} value={f.message} onChange={set("message")} className="mt-1.5" data-testid="send-message" /></div>
              <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium"><Paperclip className="h-4 w-4" /> Attachments</div>
                <p className="mt-1 text-xs text-muted-foreground">Quotation PDF ({row.code}, no cost shown) is always attached.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => openPdf(`/pipeline/proposals/${row.id}/pdf`, { view: "client" })} data-testid="send-view-pdf"><Printer className="h-4 w-4" /> View / print quotation</Button>
                  <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={() => openPdf(`/pipeline/proposals/${row.id}/pdf`, { view: "internal" })} data-testid="send-view-internal" title="Your own cost and margin - never emailed"><FileText className="h-4 w-4" /> Internal cost sheet (not sent)</Button>
                </div>
                {d?.reply_link && <p className="mt-1 text-xs text-muted-foreground">The email also has a link where the client can <b>accept</b> or <b>ask for changes</b>. Their reply shows here and notifies you.</p>}
                {n > 0 ? (
                  <label className="mt-2 flex cursor-pointer items-start gap-2">
                    <input type="checkbox" checked={f.attach} onChange={(e) => setF((x) => ({ ...x, attach: e.target.checked }))} className="mt-1 h-4 w-4 accent-[#910029]" data-testid="send-attach" />
                    <span>Attach {n} design drawing{n === 1 ? "" : "s"}<span className="block text-xs text-muted-foreground">{d.files.map((x) => `${x.filename} (${fmtSize(x.size_bytes)})`).join(" · ")}</span></span>
                  </label>
                ) : <p className="mt-2 text-xs text-amber-700">No design drawings uploaded yet.</p>}
                <button type="button" onClick={() => setFiles(true)} className="mt-2 text-xs font-medium text-[#910029] underline" data-testid="send-manage-files">Upload / manage drawings</button>
              </div>
              {err && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="send-error">{err}</p>}
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {row.status === "DRAFT" ? <Button variant="ghost" disabled={!d || !!busy} onClick={markOnly} data-testid="send-mark">{busy === "mark" && <Loader2 className="h-4 w-4 animate-spin" />} Mark as sent (no email)</Button> : <span />}
            <Button disabled={!d || !!busy || !f.to.trim()} onClick={send} data-testid="send-go">{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {files && <ProposalFilesDialog row={row} onClose={() => { setFiles(false); load(); }} onDone={onDone} />}
    </>
  );
}
